import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import { getParams, navigateTo, setParams } from "../../utils/location";
import withTranslation from "../../hocs/withTranslation";
import SignupView from "../SignupView";
import { SignupViewWithRecaptcha } from "../SignupView";
import LoginView from "../LoginView";
import VerifyCodeView from "../VerifyCodeView";
import ConsentView from "../ConsentView";
import MissingEmailView from "../MissingEmailView";
import AlreadyLoggedInView from "../AlreadyLoggedInView";
import Modal from "../Modal";
import { LOGIN_TYPES } from "../LoginView";
import {
  openAuthLogin,
  openAuthSignup,
  showVerifyCode,
  closeVerifyCode,
  loadPrivacyConsentDetails,
  showPrivacyConsent,
  showPrivacyConsentInfoModal,
  closePrivacyConsent,
  setProfileInSignupFlowMode,
  showMissingEmailModal
} from "../../store/user/actions";
import { closeAuthModal, closeAuthView } from "../../store/app/actions";
import { getStyles } from "../../store/selectors";
import { isNewUser } from "../../utils/user";
import { AuthCaseManager } from "./AuthCases";

import "slick-carousel/slick/slick.css";

const LOGIN_SIGNUP_SLIDE = 0;
const VERIFY_CODE_SLIDE = 1;

// Initialize the auth case manager
const authCaseManager = new AuthCaseManager();

function AuthView({ T, modalMode, isSignupPopupMode, onSuccess, isEmbed, ...props }) {
  const dispatch = useDispatch();
  const {
    app: { isSSR, authModalOpen, inStoreSignupBranchId, loginType, mobileAuthViewOpen },
    user: {
      authMode,
      showVerifyView,
      showPrivacyConsent: shouldShowPrivacyConsent,
      loggedIn,
      loyaltyProfile,
      consentState: { sent: consentSent },
      showPrivacyConsentInfoModal: shouldShowPrivacyConsentInfoModal,
      showMissingEmailModal: shouldShowMissingEmailModal,
      isSingUpInProgress,
      userDetails
    },
  } = useSelector(({ app, user }) => ({ app, user }));
  const app = useSelector(({ app }) => app);
  const user = useSelector(({ user }) => user);

  const [disableSignupInputs, setDisableSignupInputs] = useState(false);

  const { pageContext, location } = props;

  const sliderRef = useRef(null);

  const appStyles = getStyles({ app }, props);

  // Create context for auth case manager
  const authContext = {
    user,
    app,
    location,
    appStyles,
    pageContext,
    modalMode,
    isSignupPopupMode,
    isEmbed,
    ...props
  };

  // Get the active auth case and view type
  const activeCase = authCaseManager.getActiveCase(authContext);
  const activeViewType = authCaseManager.getActiveViewType(authContext);
  const shouldShowDialog = authCaseManager.shouldShowAnyDialog(authContext);

  // Debug logging (can be removed in production)
  console.log('AuthView Debug Info:', {
    activeCase: activeCase?.getCaseName(),
    activeViewType,
    shouldShowDialog,
    debugInfo: authCaseManager.getDebugInfo(authContext)
  });

  // Handle auth mode transitions based on active case
  useEffect(() => {
    if (!activeCase) return;

    const caseName = activeCase.getCaseName();
    
    // Set appropriate auth mode based on case
    if (caseName === 'SignupCase' && authMode !== 'signup') {
      dispatch(openAuthSignup());
    } else if (caseName === 'LoginCase' && authMode !== 'login') {
      dispatch(openAuthLogin());
    }
  }, [activeCase, authMode, dispatch]);

  // Handle slider transitions
  useEffect(() => {
    if (!sliderRef.current) return;
    
    if (activeViewType === 'verify') {
      sliderRef.current.slickGoTo(VERIFY_CODE_SLIDE);
    } else {
      sliderRef.current.slickGoTo(LOGIN_SIGNUP_SLIDE);
    }
  }, [activeViewType]);

  // Handle privacy consent loading
  useEffect(() => {
    if (activeCase?.getCaseName() === 'PrivacyConsentCase') {
      dispatch(loadPrivacyConsentDetails());
      dispatch(showPrivacyConsent());
    }
  }, [activeCase, dispatch]);

  // Handle missing email modal
  useEffect(() => {
    if (activeCase?.getCaseName() === 'MissingEmailCase') {
      dispatch(showMissingEmailModal(true));
    }
  }, [activeCase, dispatch]);

  const handleSignupToCodeTransition = () => {
    dispatch(showVerifyCode());
    sliderRef.current.slickGoTo(VERIFY_CODE_SLIDE);
  }

  const handleVerifyCodeNextTransition = (shouldStopSignUp) => {
    const { user, appStyles, isInStoreSignup } = props;

    if (isSignupPopupMode) {
      if(onSuccess) {
        onSuccess();
      }
      // In embed/popup mode, don't close auth view or navigate - let the parent handle it
      return;
    } else if (isInStoreSignup) {
      navigateTo(
        setParams("/store-thanks", {
          backPath: _.get(props, "location.pathname"),
        })
      );
    } else {
      if (_.get(appStyles, "showProfileOnSignup") && isNewUser(user)) {
        setProfileInSignupFlowMode();
        navigateTo("/profile");
      } else {
        if (
          props.loginType &&
          props.loginType !== LOGIN_TYPES.PAYMENT
        ) {
          navigateTo(`/${props.loginType}`);
        }
      }
    }

    // Check if we need to show privacy consent after verification
    const needsPrivacyConsent = activeCase?.getCaseName() === 'PrivacyConsentCase';
    needsPrivacyConsent ? dispatch(closeAuthModal(shouldStopSignUp)) : handleCloseAuthView(shouldStopSignUp);
  };

  const handleCloseAuthView = (shouldStopSignUp) => {
    window?.parent.postMessage("IFRAME:CLOSE_AUTH_VIEW", "*");

    const caseName = activeCase?.getCaseName();

    // Handle specific case closing logic
    if (caseName === 'PrivacyConsentCase') {
      if (
        !shouldShowPrivacyConsentInfoModal &&
        loginType === LOGIN_TYPES.PAYMENT &&
        !consentSent
      ) {
        dispatch(showPrivacyConsentInfoModal(true));
        return;
      }
    }

    if (caseName === 'MissingEmailCase') {
      dispatch(showMissingEmailModal(false));
      return;
    }

    // Close auth modal and view
    dispatch(closeAuthModal(shouldStopSignUp));
    dispatch(closeAuthView(shouldStopSignUp));

    // Handle navigation
    if (isSignupPopupMode) {
      console.log(`Skipping navigation isSignupPopupMode=${isSignupPopupMode}`)
    } else if (_.includes(location.pathname, "signup") ||
        _.includes(location.pathname, "login")){
      console.log(`Navigate to / isSignupPopupMode=${isSignupPopupMode}`)
      navigateTo("/");
    }
  };

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipeToSlide: false,
    swipe: false,
    touchMove: false,
    lazyLoad: true,
    initialSlide: showVerifyView ? VERIFY_CODE_SLIDE : LOGIN_SIGNUP_SLIDE,
    adaptiveHeight: true,
    afterChange: (currentSlideIndex) =>
      setDisableSignupInputs(currentSlideIndex !== LOGIN_SIGNUP_SLIDE),
  };

  const isInStoreSignup = Boolean(inStoreSignupBranchId);

  const businessAppConfiguration = pageContext.businessAppConfiguration || {};
  const { recaptchaKey, hcaptchaKey } = businessAppConfiguration;
  const loadWithSecurityProvider = (recaptchaKey || hcaptchaKey);

  const SignupViewComponent = loadWithSecurityProvider ? (
      <SignupViewWithRecaptcha
        {...{
          T,
          pageContext,
          location,
          modalMode,
          nextTransition: handleSignupToCodeTransition,
          openAuthLogin: () => dispatch(openAuthLogin()),
          disableSignupInputs,
          handleClose: handleCloseAuthView,
          isInStoreSignup,
          isSignupPopupMode
        }}
      />
  ) : (
    <SignupView
      {...{
        T,
        pageContext,
        location,
        modalMode,
        nextTransition: handleSignupToCodeTransition,
        openAuthLogin: () => dispatch(openAuthLogin()),
        disableSignupInputs,
        handleClose: handleCloseAuthView,
        isInStoreSignup,
        isSignupPopupMode
      }}
    />
  );

  // Render based on active view type
  const renderView = () => {
    switch (activeViewType) {
      case 'already-logged-in':
        return (
          <AlreadyLoggedInView
            T={T}
            appStyles={appStyles}
          />
        );
      
      case 'consent':
        return (
          <ConsentView
            T={T}
            pageContext={pageContext}
            location={location}
            nextTransition={
              !isSingUpInProgress && loginType
                ? () => dispatch(closePrivacyConsent())
                : handleCloseAuthView
            }
            modalMode={false}
          />
        );
      
      case 'missing-email':
        return (
          <MissingEmailView
            T={T}
            pageContext={pageContext}
            location={location}
            onClose={handleCloseAuthView}
            modalMode={modalMode}
          />
        );
      
      case 'verify':
        return (
          <VerifyCodeView
            {...{
              T,
              pageContext,
              location,
              modalMode,
              nextTransition: handleVerifyCodeNextTransition,
              closeVerifyCode: () => dispatch(closeVerifyCode()),
              handleClose: handleCloseAuthView,
              isInStoreSignup,
              inStoreSignupBranchId,
            }}
          />
        );
      
      case 'signup':
        return SignupViewComponent;
      
      case 'login':
      default:
        return (
          <LoginView
            {...{
              T,
              pageContext,
              location,
              modalMode,
              openAuthSignup: () => dispatch(openAuthSignup()),
              showVerifyCode: () => dispatch(showVerifyCode()),
              handleClose: handleCloseAuthView,
              nextTransition: handleSignupToCodeTransition,
            }}
          />
        );
    }
  };

  // Don't render if no dialog should be shown
  if (!shouldShowDialog) {
    return null;
  }

  // Don't render if not in modal/mobile view and not embedded
  if (!authModalOpen && !mobileAuthViewOpen && !isEmbed) {
    return null;
  }

  // For verify view, render directly without slider
  if (activeViewType === 'verify') {
    return (
      <>
        {modalMode ? (
          <Modal
            open={authModalOpen}
            onClose={() => handleCloseAuthView(true)}
            appStyles={appStyles}
            style={{ width: "100%" }}
          >
            {renderView()}
          </Modal>
        ) : (
          renderView()
        )}
      </>
    );
  }

  // For other views, use slider
  const authViewComponent = (
    <Slider {...settings} ref={sliderRef}>
      <div>
        {renderView()}
      </div>
    </Slider>
  );
  
  return (
    <>
      {modalMode ? (
        <Modal
          open={authModalOpen}
          onClose={() => handleCloseAuthView(true)}
          appStyles={appStyles}
          style={{ width: "100%" }}
        >
          {authViewComponent}
        </Modal>
      ) : (
        authViewComponent
      )}
    </>
  );
}

export default withTranslation((props) => <AuthView {...props} />);
